import { useEffect, useRef, useState } from "react";
import type { FieldValues, UseFormReturn } from "react-hook-form";

export interface UseAutoSaveOptions<TValues extends FieldValues> {
  form: UseFormReturn<TValues>;
  onSave: (values: TValues) => Promise<boolean | void> | boolean | void;
  /** Debounce in ms. Defaults to 500. */
  delayMs?: number;
  /** When true, the hook is paused entirely. */
  disabled?: boolean;
}

export interface UseAutoSaveState {
  isPending: boolean;
  isSaving: boolean;
  hasUnsavedChanges: boolean;
  errorMessage: string | null;
}

/**
 * Plain timeout-based autosave modelled after the legacy admin client.
 * Debounce value changes, save the latest snapshot, and only clear dirty state
 * if the saved snapshot still matches the form after the request resolves.
 */
export function useAutoSave<TValues extends FieldValues>({
  form,
  onSave,
  delayMs = 500,
  disabled = false,
}: UseAutoSaveOptions<TValues>): UseAutoSaveState {
  const onSaveRef = useRef(onSave);
  const previousValuesRef = useRef(cloneValues(form.getValues()));
  const latestValuesRef = useRef(cloneValues(form.getValues()));
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isSavingRef = useRef(false);
  const hasQueuedSaveRef = useRef(false);
  const [state, setState] = useState<UseAutoSaveState>({
    isPending: false,
    isSaving: false,
    hasUnsavedChanges: false,
    errorMessage: null,
  });

  useEffect(() => {
    onSaveRef.current = onSave;
  }, [onSave]);

  const runSave = async () => {
    if (disabled || isSavingRef.current) return;

    isSavingRef.current = true;
    setState((current) => ({
      isPending: false,
      isSaving: true,
      hasUnsavedChanges: current.hasUnsavedChanges,
      errorMessage: null,
    }));

    const snapshot = cloneValues(latestValuesRef.current);

    try {
      const didSave = await onSaveRef.current(snapshot);

      const currentValues = cloneValues(form.getValues());
      const hasUnsavedChanges =
        didSave === false || !areValuesEqual(currentValues, snapshot);
      setState({
        isPending: false,
        isSaving: false,
        hasUnsavedChanges,
        errorMessage: null,
      });
    } catch (error) {
      console.error("Auto-save failed", error);
      setState({
        isPending: false,
        isSaving: false,
        hasUnsavedChanges: true,
        errorMessage:
          error instanceof Error ? error.message : "Auto-save failed",
      });
    } finally {
      isSavingRef.current = false;

      if (hasQueuedSaveRef.current) {
        hasQueuedSaveRef.current = false;
        scheduleSave();
      } else {
        setState((current) => ({
          isPending: false,
          isSaving: false,
          hasUnsavedChanges: current.hasUnsavedChanges,
          errorMessage: current.errorMessage,
        }));
      }
    }
  };

  const scheduleSave = () => {
    if (disabled) return;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    setState((current) => ({
      isPending: true,
      isSaving: current.isSaving,
      hasUnsavedChanges: true,
      errorMessage: null,
    }));

    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;

      if (isSavingRef.current) {
        hasQueuedSaveRef.current = true;
        setState((current) => ({
          isPending: true,
          isSaving: true,
          hasUnsavedChanges: current.hasUnsavedChanges,
          errorMessage: current.errorMessage,
        }));
        return;
      }

      void runSave();
    }, delayMs);
  };

  useEffect(() => {
    if (disabled) {
      previousValuesRef.current = cloneValues(form.getValues());
      latestValuesRef.current = cloneValues(form.getValues());

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      isSavingRef.current = false;
      hasQueuedSaveRef.current = false;
      setState({
        isPending: false,
        isSaving: false,
        hasUnsavedChanges: false,
        errorMessage: null,
      });
      return undefined;
    }

    previousValuesRef.current = cloneValues(form.getValues());
    latestValuesRef.current = previousValuesRef.current;

    const subscription = form.watch(() => {
      const currentValues = cloneValues(form.getValues());
      latestValuesRef.current = currentValues;

      const valuesChanged = !areValuesEqual(
        previousValuesRef.current,
        currentValues
      );
      previousValuesRef.current = currentValues;

      if (!valuesChanged) return;

      const { isSubmitting } = form.formState;

      if (isSubmitting) return;

      scheduleSave();
    });

    return () => subscription.unsubscribe();
  }, [form, disabled, delayMs]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return state;
}

function areValuesEqual<TValues extends FieldValues>(
  left: TValues,
  right: TValues
) {
  return JSON.stringify(left) === JSON.stringify(right);
}

function cloneValues<TValues>(values: TValues): TValues {
  return JSON.parse(JSON.stringify(values)) as TValues;
}
